import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createQueryDefinitionAction, modifyQueryDefinitionAction } from "../../actions";
import { TextField, Grid, Select, MenuItem, FormControl, InputLabel, Button, Tooltip } from "@material-ui/core";
import { getLocalizedString } from "@icarius-localization/strings";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import useHandleForm from "./useHandleForm";
import { getActiveTypes, getDataTypes, getRoleTypes } from "../../selectors";
import { AddIcon, DeleteIcon, EditIcon } from "@icarius-icons";
import { getAppColor } from "@icarius-app/selectors";
import TextFieldMultiline from "@icarius-common/textFieldMultiline";
import AgGridContainer from "@icarius-common/commonPage/agGridContainer";
import ConfirmationDialog from "../dialogs/confirmationDialog";
import QuestionFormDialog from "../dialogs/questionFormDialog";
import { getColumnDefByPage } from "@icarius-table/columnDefs";
import CustomNumberFormat from "@icarius-common/dynamicInputs/customNumberFormat";
import PromptHistoryPanel from "../dialogs/aiAssisted/promptHistoryPanel";

const gridStyle = { height: 75, padding: "0px 10px" };

const SelectorButton = ({ text, onClick }) => {
    const color = useSelector(getAppColor);

    return (
        <Button style={{ margin: 5 }} variant={"outlined"} disableRipple={true} onClick={onClick}>
            <AddIcon htmlColor={color} />
            {text}
        </Button>
    );
};

const QueryForm = (props) => {
    const questionsGridRef = useRef(null);
    const color = useSelector(getAppColor);

    const { data, handleClose } = props;

    const dispatch = useDispatch();

    const { isAiGenerated } = data;
    const activeTypes = useSelector(getActiveTypes);
    const roleTypes = useSelector(getRoleTypes);
    const dataTypes = useSelector(getDataTypes);

    const create = (formData) => {
        dispatch(createQueryDefinitionAction(formData)).then((res) => res?.status === 200 && handleClose());
    };

    const modify = (formData) => {
        dispatch(modifyQueryDefinitionAction(formData)).then((res) => res?.status === 200 && handleClose());
    };

    const openValidationError = () => {
        dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
    };

    const openSelectorValidationError = () => {
        dispatch(
            openSnackbarAction({ msg: getLocalizedString("invalidSelectors"), severity: "error", duration: 10000 })
        );
    };

    const { isCreate, formData, setFormValue, submit } = useHandleForm(
        data,
        create,
        modify,
        dataTypes,
        openValidationError,
        openSelectorValidationError
    );

    const [questionDialogState, setQuestionDialogState] = useState({ open: false, index: null, data: null });
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

    const handleOpenQuestionDialog = (data = null, index = null) => {
        setQuestionDialogState({ open: true, index, data });
    };

    const handleCloseQuestionDialog = () => {
        setQuestionDialogState({ open: false, index: null, data: null });
    };

    const handleSaveQuestion = (questionData) => {
        const newQuestions = [...(formData.questions || [])];
        if (questionDialogState.index !== null) {
            newQuestions[questionDialogState.index] = questionData;
        } else {
            newQuestions.push(questionData);
        }
        setFormValue(newQuestions, "questions");
        handleCloseQuestionDialog();
    };

    const handleOpenConfirmDialog = () => {
        const selectedNodes = questionsGridRef.current?.api?.getSelectedNodes();
        if (!selectedNodes || selectedNodes.length === 0) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustselectItem"), severity: "warning" }));
            return;
        }
        setOpenConfirmDialog(true);
    };

    const handleCloseConfirmDialog = () => {
        setOpenConfirmDialog(false);
    };

    const handleDeleteSelectedQuestions = () => {
        if (questionsGridRef.current?.api) {
            const selectedNodes = questionsGridRef.current.api.getSelectedNodes();
            const selectedIndexes = selectedNodes.map((node) => parseInt(node.id));
            const newQuestions = (formData.questions || []).filter((_, index) => !selectedIndexes.includes(index));
            setFormValue(newQuestions, "questions");
            questionsGridRef.current.api.deselectAll();
        }
        handleCloseConfirmDialog();
    };

    const handleEditSelectedQuestion = () => {
        const selectedNodes = questionsGridRef.current?.api?.getSelectedNodes();
        if (!selectedNodes || selectedNodes.length !== 1) {
            dispatch(
                openSnackbarAction({
                    msg: getLocalizedString("mustSelectOneItem") || "Debe seleccionar un único ítem para editar",
                    severity: "warning",
                })
            );
            return;
        }
        const node = selectedNodes[0];
        handleOpenQuestionDialog(node.data, parseInt(node.id));
    };

    return (
        <Grid container item xs={12} direction="row" style={{ width: "95%", margin: "auto" }}>
            <Grid container item xs={12}>
                <Grid container item alignItems="center" xs={12} sm={6} md={4} style={gridStyle}>
                    {" "}
                    {/* codigo */}
                    <TextField
                        disabled={!Boolean(isCreate)}
                        required
                        fullWidth
                        margin={"none"}
                        label={getLocalizedString("codeAlt")}
                        onChange={(e) => setFormValue(e.target.value, "code")}
                        value={formData.code}
                        inputProps={{ maxLength: 20 }}
                    />
                </Grid>
                <Grid container item alignItems="center" xs={12} sm={6} md={4} style={gridStyle}>
                    {" "}
                    {/* nombre */}
                    <TextField
                        required
                        fullWidth
                        margin={"none"}
                        label={getLocalizedString("name")}
                        value={formData.name}
                        onChange={(e) => setFormValue(e.target.value, "name")}
                        inputProps={{ maxLength: 254 }}
                    />
                </Grid>
                <Grid container item alignItems="center" xs={12} sm={6} md={4} style={gridStyle}>
                    {" "}
                    {/* Activa */}
                    <FormControl style={{ width: "100%" }}>
                        <InputLabel required id={`label-active`}>
                            {getLocalizedString("activeFemale")}
                        </InputLabel>
                        <Select
                            value={formData.active}
                            labelId={`label-active`}
                            id={`select-active`}
                            onChange={(e) => setFormValue(e.target.value, "active")}
                            margin={"none"}
                        >
                            {activeTypes &&
                                activeTypes.map((item) => (
                                    <MenuItem className={"whiteText"} key={item.key} value={item.key}>
                                        {item.value}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </Grid>
                {data.promptHistory && (
                    <Grid container item alignItems="center" xs={12} style={{ padding: "16px 0px 0px", marginTop: 8 }}>
                        {" "}
                        {/* historial de indicaciones */}
                        <PromptHistoryPanel promptHistory={data.promptHistory} />
                    </Grid>
                )}
                <Grid container item alignItems="center" xs={12} style={{ padding: "20px 10px" }}>
                    {" "}
                    {/* sql */}
                    <TextFieldMultiline
                        required
                        fullWidth
                        label="SQL"
                        rows={25}
                        margin={"none"}
                        value={formData.sql}
                        onChange={(e) => setFormValue(e.target.value, "sql")}
                        inputProps={{ spellCheck: "false" }}
                    />
                </Grid>
                <Grid container item alignItems="center" sm={12} md={6} style={{ ...gridStyle, marginTop: 12 }}>
                    {" "}
                    {/* group */}
                    <TextField
                        required
                        fullWidth
                        margin={"none"}
                        label={getLocalizedString("locationGroup")}
                        value={formData.group}
                        onChange={(e) => setFormValue(e.target.value, "group")}
                        inputProps={{ maxLength: 100 }}
                        helperText={getLocalizedString(
                            "specialLocationGroupsMyStaffPeopleMasterPersonalDataUserMenuAndTest"
                        )}
                    />
                </Grid>
                <Grid container item alignItems="center" xs={12} sm={6} md={4} style={gridStyle}>
                    {" "}
                    {/* role */}
                    <FormControl style={{ width: "100%" }}>
                        <InputLabel required id={`label-role`}>
                            {getLocalizedString("viewRole")}
                        </InputLabel>
                        <Select
                            value={formData.rol}
                            labelId={`label-role`}
                            id={`select-role`}
                            onChange={(e) => setFormValue(e.target.value, "rol")}
                            margin={"none"}
                        >
                            {roleTypes &&
                                roleTypes.map((item) => (
                                    <MenuItem className={"whiteText"} key={item.key} value={item.key}>
                                        {item.value}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid container item alignItems="center" xs={12} sm={6} md={2} style={gridStyle}>
                    {" "}
                    {/* secuencia */}
                    <TextField
                        required
                        fullWidth
                        margin={"none"}
                        label={getLocalizedString("appearanceSequence")}
                        InputProps={{ inputComponent: CustomNumberFormat }}
                        value={formData.sequence}
                        onChange={(e) => setFormValue(e.target.value, "sequence")}
                        inputProps={{
                            min: 1,
                            max: 999,
                            maxLength: 3,
                            isAllowed: (v) => v.floatValue === undefined || v.floatValue <= 999,
                        }}
                    />
                </Grid>
                <Grid container item alignItems="center" xs={12} style={{ padding: "0px 10px", paddingTop: 20 }}>
                    {" "}
                    {/* selectores */}
                    <TextField
                        required
                        fullWidth
                        helperText={`${getLocalizedString("sqlSelectorsCouldBe")}: ${dataTypes
                            .map((item) => item.key)
                            .join(", ")}`}
                        margin={"none"}
                        label={getLocalizedString("columnSelectorTypes")}
                        value={formData.selectors}
                        onChange={(e) => setFormValue(e.target.value, "selectors")}
                    />
                </Grid>
                <Grid container item alignItems="center" justify="center" xs={12} style={{ marginTop: 10 }}>
                    {dataTypes.map((item) => {
                        return (
                            <SelectorButton
                                key={item.key}
                                text={item.value}
                                onClick={() => setFormValue(`${formData.selectors},${item.key}`, "selectors")}
                            />
                        );
                    })}
                </Grid>
            </Grid>
            {!isAiGenerated && (
                <Grid container item alignItems="center" xs={12} style={{ padding: "20px 10px" }}>
                    <TextFieldMultiline
                        fullWidth
                        label={getLocalizedString("sqlForContextManagement")}
                        rows={10}
                        margin={"none"}
                        value={formData.sqlContext}
                        onChange={(e) => setFormValue(e.target.value, "sqlContext")}
                        inputProps={{ spellCheck: "false" }}
                    />
                </Grid>
            )}
            {!isAiGenerated && (
                <Grid container item alignItems="center" xs={12} style={{ padding: "20px 10px" }}>
                    <TextFieldMultiline
                        fullWidth
                        label={getLocalizedString("contextComments")}
                        rows={10}
                        margin={"none"}
                        value={formData.comment}
                        onChange={(e) => setFormValue(e.target.value, "comment")}
                        inputProps={{ spellCheck: "false" }}
                    />
                </Grid>
            )}
            <Grid container item alignItems="center" xs={12} style={{ padding: "20px 10px", marginTop: 10 }}>
                <Grid
                    item
                    xs={12}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}
                >
                    <h3 style={{ margin: 0, color: "var(--mainText)", fontWeight: "normal", fontSize: "1rem" }}>
                        {getLocalizedString("defaultQuestions")}
                    </h3>
                    <div style={{ display: "flex", gap: 10 }}>
                        <Tooltip title={getLocalizedString("add")}>
                            <Button variant={"outlined"} onClick={() => handleOpenQuestionDialog()}>
                                <AddIcon htmlColor={color} />
                            </Button>
                        </Tooltip>
                        <Tooltip title={getLocalizedString("modify")}>
                            <Button variant={"outlined"} onClick={handleEditSelectedQuestion}>
                                <EditIcon htmlColor={color} />
                            </Button>
                        </Tooltip>
                        <Tooltip title={getLocalizedString("delete")}>
                            <Button variant={"outlined"} onClick={handleOpenConfirmDialog}>
                                <DeleteIcon htmlColor={color} />
                            </Button>
                        </Tooltip>
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <AgGridContainer
                        ref={questionsGridRef}
                        ownColumnDef={getColumnDefByPage("queryDefinitionQuestions")}
                        rowData={formData.questions || []}
                        height={250}
                        hasFixedFilters={false}
                    />
                </Grid>
            </Grid>
            <Grid container item xs={12} justify={"flex-end"} alignItems="center" style={{ marginBottom: 35 }}>
                <ButtonDialogAction isAccept onClick={submit} text={getLocalizedString("save")} />
            </Grid>
            <ConfirmationDialog
                open={openConfirmDialog}
                handleClose={handleCloseConfirmDialog}
                handleConfirm={handleDeleteSelectedQuestions}
                title={getLocalizedString("delete")}
                text={getLocalizedString("areYouSureYouWantToDeleteTheQuestion")}
            />
            <QuestionFormDialog
                open={questionDialogState.open}
                handleClose={handleCloseQuestionDialog}
                handleSave={handleSaveQuestion}
                initialData={questionDialogState.data}
                questions={formData.questions || []}
                editIndex={questionDialogState.index}
            />
        </Grid>
    );
};

export default QueryForm;
